import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {select, Store} from '@ngrx/store';
import {Observable} from 'rxjs/Observable';
import {filter, map, merge, tap, withLatestFrom, takeUntil} from 'rxjs/operators';
import {selectActiveWorkflow ,selectRequestedRevision, selectTestElapsedTime} from "@ispm/workflow/store/workflow-config.selectors";
import {selectUiConfiguration} from "../../../configuration/store/configuration.selector";
import {has, cloneDeep} from 'lodash';
import { Subject } from 'rxjs';
import { WorkflowBarItem } from '@ispm/shared/store/shared.reducer';


@Component({
  selector: 'gen-workflow-bar',
  templateUrl: './workflow-bar.component.html',
  styleUrls: ['./workflow-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowBarComponent implements OnInit, OnDestroy {

  private store: any;
  private _alive = true;
  private destroy$ = new Subject();

  public steps$: Observable<Array<any>>;
  public activeIndex: number;
  public revision;
  public testElapsedTime;
  public stepTagline;
  @Input() stepTaglineField: any;
  @Output()
  public onNotSupported: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private _changeDetector: ChangeDetectorRef,
              store: Store<IgenomicsState>) {
    this.store = store;
    this.steps$ = store.pipe(
      select(selectActiveWorkflow),
      merge(
        store.pipe(select('shared'))
      ),
      withLatestFrom(
        store.pipe<any>(select('shared')),
        store.pipe<any>(select(selectTestElapsedTime)),
        store.pipe(select(selectUiConfiguration('workflowVisibility.barItems', 'Test'))),
        store.pipe(select(selectRequestedRevision))
      ),
      map(([streamSource, workflowBarOverride, testElapsedTime, items, revision]) => {
        const emptyState = {items: null, status: null};
        this.revision = cloneDeep(revision);
        workflowBarOverride = cloneDeep(workflowBarOverride);
        items = cloneDeep(items);
        this.testElapsedTime = parseInt(testElapsedTime, 10);
        if (!(revision && items)) {
          return emptyState;
        } else if (!has(revision, 'ui.workflowVisibility.barItems')) {
          this.emitForNotSupported(true);
          return emptyState;
        } else if (workflowBarOverride.workflow_bar) {
          // in case of state override by ui actions (mostly used by revise for preview)
          return {
            items: workflowBarOverride.workflow_bar,
            status: workflowBarOverride.status
          };
        } else {
          return {
            items: this.formatItems(items, revision),
            status: revision.status
          };
        }
      }),
      filter(({items}) => !!items),
      tap(() => this.emitForNotSupported(false)),
      map(({items, status}) => {
        if (items && items.length > 0) {
          if (items.length == 1) {
            items[0].singleStep = true;
          }
          items[0].pos = 'first';
          items[items.length - 1].pos = 'last';
          this.activeIndex = items.findIndex((e) => e.isActive);
          if (!!status && items[this.activeIndex].fromStatus) {
            items[this.activeIndex].name = status;
          }
        }
        return items;
      }),
      tap(() => {
        if (this._alive) {
          this._changeDetector.markForCheck();
          this._changeDetector.detectChanges();
        }
      }),
      takeUntil(this.destroy$)
    );

  }

  ngOnInit() {
    if (this.stepTaglineField) {
      this.store.pipe(select((state: any) => state.tests.diagnosticOrders.order[this.stepTaglineField])).takeUntil(this.destroy$).subscribe((status) => {
        this.stepTagline = status;
        this._changeDetector.markForCheck();
        this._changeDetector.detectChanges();
      });
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._changeDetector.detach();
    this._alive = false;
  }

  public trackByFn(item, index) {
    return index;
  }

  private formatItems(uiItems: WorkflowBarItem[], revision, keepItem = false) {
    if (!uiItems) { return null; }
    return  uiItems.filter((uiItem) => {
      if (uiItem.isActive) {
        if (revision.humanReadableMessage) {
          uiItem.tooltip = revision.humanReadableMessage;
        }
        if (['canceled', 'failed', 'cancelled', 'stopped'].includes(revision.status.toLowerCase())) {
          uiItem.name = revision.status;
          uiItem.fromStatus = true;
        }
      }
      if (uiItem.conditionPrevStepDone) {
        for (const revItem of revision.items) {
          if (revItem.name === uiItem.conditionPrevStepDone) {
            if (revItem.status.toLowerCase() !== "done")
              return false;
            if (!keepItem)
              delete uiItem.conditionPrevStepDone;
            break;
          }
        }
      }
      return true;
    });
  }

  private emitForNotSupported(status: boolean) {
    this.onNotSupported.emit(status);
  }
}

export enum workflowVisibilityEnum {
  show,
  hide,
  showWhenActive,
  showAfterActive
}
